import SvgIcon from "@/components/common/SvgIcon";
import { Checkbox } from "@/components/kit";
import { Table } from "@/components/kit/Table/Table";

import classNames from "classnames";
import { ChangeEvent, useMemo, useState } from "react";
import { TUserParamsType } from "../../types";
import { useStoreOrg } from "../../store/useStoreOrganization";
import UserRow from "./UserRow";
import useStoreMe from "@/hooks/store/useStoreMe";
import { IUserResponse } from "@/features/admins/types";
import { useGetTrioRoles } from "@/hooks/query/acl/useGetTrioRoles";

const fields: {
  widths: string | number;
  title: string;
  visibility: boolean;
  index: number;
  orderBy?: string;
}[] = [
  {
    index: 0,
    title: "Name",
    widths: "33.333%",
    visibility: true,
    orderBy: "NAME",
  },
  { index: 1, title: "Email", widths: "33.333%", visibility: true },
  { index: 2, title: "Access role", widths: "33.333%", visibility: true },
];

type TProps = {
  filter: Pick<TUserParamsType, "orderBy" | "orderDirection">;
  setFilter: (
    data: Pick<TUserParamsType, "orderBy" | "orderDirection">,
  ) => void;
  users: IUserResponse[];
  isNavigate?: boolean;
  isLoading?: boolean;
  departmentName?: string;
};

const UsersTable = ({ users, filter, setFilter, isLoading }: TProps) => {
  const { users: usersList, setState } = useStoreOrg();
  const { me } = useStoreMe();
  const { data: roles } = useGetTrioRoles();
  const roleOptions = useMemo(() => {
    return (
      roles?.map((item) => ({
        title: item.name,
        value: item.id,
      })) ?? []
    );
  }, [roles]);
  const [columnWidths] = useState<string[]>(
    // fields.map((elm) => Number(elm.widths)),
    fields.map((elm) => elm.widths) as string[],
  );

  // const { setState, users: usersList } = useStoreOrg();

  // const handleSelectAll = () => {
  //   const mapAllUser = users.map((item) => ({ id: item.id, roleId: item.id }));
  //   if (mapAllUser.length === usersList.length) {
  //     setState("users", []);
  //   } else {
  //     setState("users", mapAllUser);
  //   }
  // };

  // const handleSelectDevice = (device: TDevice) => {
  //   if (selectDevices.includes(device)) {
  //     setState(
  //       "devices",
  //       selectDevices.filter((item) => item.id !== device.id),
  //     );
  //   } else {
  //     setState("devices", [...selectDevices, device]);
  //   }
  // };

  const handleAddAll = (e: ChangeEvent<HTMLInputElement>) => {
    const filterUsers = users.filter((item) => {
      return item.id !== me?.id;
    });

    if (e.target.checked) {
      const updatedUsers = filterUsers.map((item) => ({
        id: item.id,
        roleId: usersList.find((i) => i.id == item.id)?.roleId || "",
      }));

      setState("users", updatedUsers);
    } else {
      setState("users", []);
    }
  };

  if (!isLoading && !users?.length) return null;

  return (
    <Table.Root
      defaultColumnConfig={fields}
      columnConfig={fields}
      columnWidths={columnWidths}
      columnControl={false}
    >
      <Table.Header>
        <Table.RowHeader>
          {fields.map((elm) => (
            <Table.ColumnHeader
              key={elm.index}
              name={elm.title}
              hasAction={false}
              moveable={false}
              hasColumnControl={false}
              index={elm.index}
              className={
                elm.title === "Action" ? `!order-${fields.length}` : ""
              }
            >
              <div
                title={elm.title}
                className="truncate !font-bold flex items-center gap-6 !text-sm"
              >
                {elm.title === "Name" && (
                  <div className="border-e flex items-center h-[42px] justify-end pe-2 ">
                    <Checkbox
                      checked={users.every((item) =>
                        usersList.find((user) => user.id === item.id),
                      )}
                      size="md"
                      onClick={(e) => e.stopPropagation()}
                      onChange={handleAddAll}
                      id="all"
                      className=""
                    />
                  </div>
                )}
                {elm.title !== "Action" ? elm.title : ""}
              </div>
              {elm.orderBy && filter.orderBy === elm.orderBy && (
                <SvgIcon
                  iconName="linearArrow"
                  fill={
                    filter.orderBy === elm.orderBy
                      ? "fill-gray-500"
                      : "fill-gray-300"
                  }
                  className={classNames({
                    "rotate-180":
                      filter.orderBy === elm.orderBy &&
                      filter.orderDirection === "DESC",
                  })}
                />
              )}
              <Table.ColumnHeaderAction
                removeable={elm.index !== 0 && elm.title !== "Action"}
                lists={
                  elm.orderBy
                    ? [
                        {
                          id: "ascending",
                          title: "Sort ascending",
                          icon: "sortFromBottomToTop",
                          visible: !(
                            filter.orderDirection === "ASC" &&
                            filter.orderBy === elm.orderBy
                          ),
                          onClick: () =>
                            setFilter({
                              ...filter,
                              orderBy: elm.orderBy as any,
                              orderDirection: "ASC",
                            }),
                        },
                        {
                          id: "descending",
                          title: "Sort descending",
                          icon: "sortFromTopToBottom",
                          visible: !(
                            filter.orderDirection === "DESC" &&
                            filter.orderBy === elm.orderBy
                          ),
                          onClick: () =>
                            setFilter({
                              ...filter,
                              orderBy: elm.orderBy as any,
                              orderDirection: "DESC",
                            }),
                        },
                      ]
                    : []
                }
                index={elm.index}
              />
            </Table.ColumnHeader>
          ))}
        </Table.RowHeader>
      </Table.Header>
      <Table.Body isLoading={isLoading}>
        {users?.map((user) => (
          <UserRow rolesOptions={roleOptions} user={user} key={user.id} />
        ))}
      </Table.Body>
    </Table.Root>
  );
};

export default UsersTable;
